/**
 * The deferred "send the reviewed reply" action for Candidate Requests.
 *
 * Sits behind the board's shared 10-second countdown (`usePendingActions`), so the proxy is not
 * called at all while the banner is up — "Stop" means the reply was never sent, not that it was
 * recalled. Split out of RequestsTab for the same reason `moveEmailAction`/`offerEmailAction` are
 * separate: to keep that file under the line limit.
 *
 * The analytics events carry the CATEGORY and draft source only — never the candidate, their
 * address, or a word of the text (see the PII rule in CLAUDE.md).
 */
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import type { InboundRequest } from '../../types/taInboundRequests';
import { updateRequestsCache } from './requestsCache';
import { draftAcceptedVerbatim } from './boardAnalytics';
import type { PendingAction } from './usePendingActions';

export function replyAction({
  item,
  bodyHtml,
  gmailToken,
  myEmail,
  onSent,
  onExpired,
  onFailed,
  onStopped,
}: {
  item: InboundRequest;
  bodyHtml: string;
  gmailToken: string | null;
  myEmail: string;
  /** The thread left the queue: sending IS handling it, so it should not come back. */
  onSent: (threadId: string) => void;
  onExpired: () => void;
  onFailed: () => void;
  onStopped: () => void;
}): Omit<PendingAction, 'secondsLeft'> {
  return {
    id: `reply:${item.threadId}`,
    label: `Sending your reply to ${item.candidateName}`,
    run: () => {
      void (async () => {
        try {
          const result = await api.sendRequestReply({
            threadId: item.threadId,
            opportunityId: item.opportunityId,
            gmailToken: gmailToken || '',
            body: bodyHtml,
          });
          // A resolved promise is not success: the proxy can answer ok:false, and claiming "sent"
          // then would leave a candidate unanswered while the card disappears.
          if (!result?.ok) {
            throw new Error('Gmail did not confirm the send — nothing went out, try again.');
          }
          updateRequestsCache(myEmail, (list) =>
            list.filter((i) => i.threadId !== item.threadId)
          );
          onSent(item.threadId);
          void logAnalyticsEvent('ta_requests_reply_sent', {
            category: item.category,
            draftSource: item.draftSource,
            /** Only the VERDICT travels — never the draft, the edit, or any part of either. */
            had_draft: Boolean(item.draft),
            accepted_verbatim: draftAcceptedVerbatim(item.draft, bodyHtml),
          });
          /**
           * Say what happened to the mailbox as well as to the candidate. A reply now also marks the
           * thread read, labels it `Spaces` and takes it out of the inbox — and when a stale token
           * stops that, the recruiter is told, because otherwise the thread quietly stays in their
           * inbox looking unanswered. Never an error toast: the reply itself went out.
           */
          const tidied = result.triaged;
          /**
           * An unrecorded reply outranks anything the mailbox did.
           *
           * `recorded === false` means the send succeeded but our `answeredAt` never landed, so the
           * thread returns to the queue looking unanswered — and the next recruiter to open it sends
           * the candidate a second reply to one question, which is the exact double-handling this
           * tab exists to end. Explicitly `=== false`: an older proxy omits the field.
           */
          if (result.recorded === false) {
            showToast({
              status: 'info',
              description: `Reply sent to ${item.candidateName}, but it could not be recorded — the request may come back. Do not send again.`,
            });
            return;
          }
          showToast(
            tidied === 'needs_reconnect'
              ? {
                  status: 'info',
                  description: `Reply sent to ${item.candidateName} — reconnect Gmail to also clear it from your inbox.`,
                }
              : tidied === 'failed'
                ? {
                    status: 'info',
                    description: `Reply sent to ${item.candidateName} — the thread is still in your inbox.`,
                  }
                : tidied === 'updated'
                  ? {
                      status: 'success',
                      description: `Reply sent to ${item.candidateName}, and the thread is archived.`,
                    }
                  : { status: 'success', description: `Reply sent to ${item.candidateName}.` }
          );
        } catch (e) {
          /**
           * The proxy refused a second reply to this message. The candidate HAS been answered, so
           * the card retires exactly as it would on a successful send — leaving it in the queue
           * would invite the recruiter to try again, which is the double-reply this prevents.
           */
          if (e instanceof api.DuplicateReply) {
            updateRequestsCache(myEmail, (list) =>
              list.filter((i) => i.threadId !== item.threadId)
            );
            onSent(item.threadId);
            void logAnalyticsEvent('ta_requests_reply_duplicate', { category: item.category });
            showToast({ status: 'info', description: e.message });
            return;
          }
          if (e instanceof api.GmailSessionExpired) {
            onExpired();
            showToast({
              status: 'error',
              description: 'Your Gmail access expired — reconnect above, then send again.',
            });
            return;
          }
          onFailed();
          void logAnalyticsEvent('ta_requests_reply_send_failed', { category: item.category });
          showToast({
            status: 'error',
            description: e instanceof Error ? e.message : 'Could not send the reply.',
          });
        }
      })();
    },
    undo: () => {
      onStopped();
      // The tab's clearest "we nearly sent something wrong" signal.
      void logAnalyticsEvent('ta_requests_send_stopped', { category: item.category, draftSource: item.draftSource });
      showToast({
        status: 'info',
        description: `Stopped — nothing was sent to ${item.candidateName}.`,
      });
    },
  };
}

/**
 * Fetch the WHOLE thread for one card.
 *
 * Lives beside `replyAction` rather than in RequestsTab for the same reason: that file is at its
 * line ceiling. An expired token raises the tab's reconnect banner; any other failure is rethrown
 * so the card falls back to the slice it already holds.
 */
export async function loadRequestThread(
  item: InboundRequest,
  gmailToken: string | null,
  onExpired: () => void
) {
  try {
    const { conversation } = await api.fetchRequestThread(item.threadId, gmailToken || '');
    return conversation;
  } catch (e) {
    if (e instanceof api.GmailSessionExpired) onExpired();
    throw e;
  }
}

/**
 * The deferred "FINN cancels the interview" action for an explicit cancellation request.
 *
 * Same shape as `replyAction`: behind the board's 10-second countdown, so "Stop" means the route
 * was never called. The countdown is UX, not the safety — the server's atomic transition is what
 * guarantees one cancellation notice however many clicks arrive.
 *
 * Deliberately does NOT retire the card: cancelling the interview is not answering the email. The
 * recruiter still sends the reply ("done — I've cancelled it"), and the card is where they do that.
 */
export function cancelInterviewAction({
  item,
  bookingRef,
  onDone,
  onStopped,
}: {
  item: InboundRequest;
  bookingRef: string;
  /** Called with what happened, so the tab can update the scheduling box copy. */
  onDone: (outcome: 'cancelled' | 'already_cancelled' | 'failed' | 'demo') => void;
  onStopped: () => void;
}): Omit<PendingAction, 'secondsLeft'> {
  return {
    id: `cancel-interview:${item.threadId}`,
    label: `Cancelling ${item.candidateName}'s interview`,
    run: () => {
      void (async () => {
        try {
          const result = await api.cancelInterviewBooking(bookingRef);
          if (result.demo) {
            onDone('demo');
            showToast({ status: 'info', description: 'Demo mode — no real interview was cancelled.' });
            return;
          }
          if (result.alreadyCancelled) {
            // Candidate or colleague got there first. The email they asked for went out ONCE.
            onDone('already_cancelled');
            void logAnalyticsEvent('ta_requests_interview_cancel_duplicate', { category: item.category });
            showToast({ status: 'info', description: 'This interview was already cancelled.' });
            return;
          }
          if (!result.cancelled) throw new Error('The interview was not cancelled — try again.');
          onDone('cancelled');
          void logAnalyticsEvent('ta_requests_interview_cancelled', { category: item.category });
          showToast({
            status: 'success',
            description: `${item.candidateName}'s interview is cancelled — now send them a quick confirmation.`,
          });
        } catch (e) {
          onDone('failed');
          void logAnalyticsEvent('ta_requests_interview_cancel_failed', { category: item.category });
          showToast({
            status: 'error',
            description: e instanceof Error ? e.message : 'Could not cancel the interview.',
          });
        }
      })();
    },
    undo: () => {
      onStopped();
      void logAnalyticsEvent('ta_requests_interview_cancel_stopped', { category: item.category });
      showToast({
        status: 'info',
        description: `Stopped — ${item.candidateName}'s interview was not cancelled.`,
      });
    },
  };
}
